import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import {
  IzzyIzzyActionEntity,
  IzzyIzzyActionCategoryEntity,
  IzzyOrganizationActionEntity,
  IzzyMetadataEngine
} from 'mj_generatedentities';
import { BCSaaSPlanEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

/**
 * Represents an action card in the gallery
 */
interface ActionCard {
  izzyAction: IzzyIzzyActionEntity;
  category: IzzyIzzyActionCategoryEntity | null;
  planName: string;
  planLevel: number;
  isAvailable: boolean;
  isConfigured: boolean;
  isPrivate: boolean;
}

/**
 * Category tree node
 */
interface CategoryNode {
  category: IzzyIzzyActionCategoryEntity;
  children: CategoryNode[];
  actions: ActionCard[];
  isExpanded: boolean;
}

/**
 * Component state for persistence
 */
interface ActionGalleryState {
  selectedOrganizationId?: string;
  selectedCategoryId?: string;
  searchTerm?: string;
}

/**
 * Action Gallery Component
 *
 * Displays all available Izzy actions organized by category with:
 * - Hierarchical category tree navigation
 * - Action cards with plan tier badges
 * - Search across action names/descriptions
 * - Quick-add to organization
 * - Filtering by category, plan level, status
 */
@Component({
  standalone: false,
  selector: 'izzy-action-gallery',
  templateUrl: './action-gallery.component.html',
  styleUrls: ['./action-gallery.component.css']
})
export class ActionGalleryComponent implements OnInit, OnDestroy, IAdminPanel {

  // ========== Inputs/Outputs ==========
  @Input() initialState: ActionGalleryState | null = null;
  @Input() organizationId: string | null = null;
  @Output() stateChange = new EventEmitter<ActionGalleryState>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() actionSelected = new EventEmitter<{ izzyAction: IzzyIzzyActionEntity; action: 'add' | 'view' }>();

  constructor() {}

  // ========== Data ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected plans: BCSaaSPlanEntity[] = [];
  protected categories: IzzyIzzyActionCategoryEntity[] = [];
  protected actions: IzzyIzzyActionEntity[] = [];
  protected organizationActions: IzzyOrganizationActionEntity[] = [];
  protected categoryTree: CategoryNode[] = [];
  protected actionCards: ActionCard[] = [];

  // ========== Selection State ==========
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedCategory: IzzyIzzyActionCategoryEntity | null = null;
  protected organizationPlanLevel: number = 0;

  // ========== UI State ==========
  protected isLoading: boolean = true;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchTerm: string = '';
  protected showUnavailable: boolean = false;

  // ========== Cleanup ==========
  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();

    // Apply initial state
    if (this.initialState) {
      if (this.initialState.selectedOrganizationId) {
        const org = this.organizations.find(o => o.ID === this.initialState!.selectedOrganizationId);
        if (org) {
          this.selectOrganization(org);
        }
      }
      if (this.initialState.selectedCategoryId) {
        const cat = this.categories.find(c => c.ID === this.initialState!.selectedCategoryId);
        if (cat) {
          this.selectCategory(cat);
        }
      }
      if (this.initialState.searchTerm) {
        this.searchTerm = this.initialState.searchTerm;
      }
    } else if (this.organizationId) {
      // Use input organization
      const org = this.organizations.find(o => o.ID === this.organizationId);
      if (org) {
        this.selectOrganization(org);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Initialize metadata
      await IzzyMetadataEngine.Instance.Config(false);

      // Load from cache
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.plans = IzzyMetadataEngine.Instance.Plans;
      this.categories = IzzyMetadataEngine.Instance.IzzyActionCategories;
      this.actions = IzzyMetadataEngine.Instance.IzzyActions;

      // Build category tree
      this.buildCategoryTree();

      // Build action cards
      this.buildActionCards();

      // Auto-select first org if none selected
      if (!this.selectedOrganization && this.organizations.length > 0) {
        this.selectOrganization(this.organizations[0]);
      }

      // Emit default status (action gallery doesn't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading action gallery data:', error);
      this.errorMessage = 'Failed to load data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private buildCategoryTree(): void {
    // Get root categories (no parent)
    const rootCategories = this.categories.filter(c => !c.ParentID);

    this.categoryTree = rootCategories.map(cat => this.buildCategoryNode(cat));
  }

  private buildCategoryNode(category: IzzyIzzyActionCategoryEntity): CategoryNode {
    const children = this.categories
      .filter(c => c.ParentID === category.ID)
      .map(c => this.buildCategoryNode(c));

    const actions = this.actions
      .filter(a => a.CategoryID === category.ID)
      .map(a => this.createActionCard(a, category));

    return {
      category,
      children,
      actions,
      isExpanded: true
    };
  }

  private buildActionCards(): void {
    this.actionCards = this.actions.map(action => {
      const category = this.categories.find(c => c.ID === action.CategoryID) || null;
      return this.createActionCard(action, category);
    });
  }

  private createActionCard(action: IzzyIzzyActionEntity, category: IzzyIzzyActionCategoryEntity | null): ActionCard {
    const plan = this.getPlanForLevel(action.MinimumPlanLevel);
    const isConfigured = this.organizationActions.some(oa => oa.ActionID === action.ActionID);

    return {
      izzyAction: action,
      category,
      planName: plan?.Name || 'Free',
      planLevel: action.MinimumPlanLevel,
      isAvailable: this.organizationPlanLevel >= action.MinimumPlanLevel || !action.IsPublic,
      isConfigured,
      isPrivate: !action.IsPublic
    };
  }

  private getPlanForLevel(level: number): BCSaaSPlanEntity | null {
    // Find the plan with matching level
    return this.plans.find(p => p.Level === level) || null;
  }

  // ========== Organization Selection ==========

  selectOrganization(org: mjBizAppsCommonOrganizationEntity): void {
    this.selectedOrganization = org;

    // Get organization's plan level via subscription chain (post-BAC: Plan no longer
    // lives on Organization directly — see IzzyMetadataEngine.GetOrganizationPlan)
    const plan = IzzyMetadataEngine.Instance.GetOrganizationPlan(org.ID);
    this.organizationPlanLevel = plan?.Level || 0;

    // Load organization's configured actions
    this.organizationActions = IzzyMetadataEngine.Instance.GetOrganizationActions(org.ID);

    // Rebuild action cards with availability info
    this.buildActionCards();

    this.emitStateChange();
  }

  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectOrganization(org);
    }
  }

  // ========== Category Selection ==========

  selectCategory(category: IzzyIzzyActionCategoryEntity | null): void {
    this.selectedCategory = category;
    this.emitStateChange();
  }

  toggleCategory(node: CategoryNode): void {
    node.isExpanded = !node.isExpanded;
  }

  // ========== Filtering ==========

  get filteredActionCards(): ActionCard[] {
    let result = this.actionCards;

    // Filter by category
    if (this.selectedCategory) {
      const categoryIds = this.getAllCategoryIds(this.selectedCategory.ID);
      result = result.filter(card => card.category && categoryIds.includes(card.category.ID));
    }

    // Filter by search term
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(card =>
        card.izzyAction.Name.toLowerCase().includes(term) ||
        (card.izzyAction.Description?.toLowerCase().includes(term))
      );
    }

    // Filter by availability
    if (!this.showUnavailable) {
      result = result.filter(card => card.isAvailable);
    }

    // Filter active only
    result = result.filter(card => card.izzyAction.Status === 'Active');

    return result;
  }

  private getAllCategoryIds(categoryId: string): string[] {
    const ids = [categoryId];
    const children = this.categories.filter(c => c.ParentID === categoryId);
    for (const child of children) {
      ids.push(...this.getAllCategoryIds(child.ID));
    }
    return ids;
  }

  onSearchChange(): void {
    this.emitStateChange();
  }

  // ========== Action Operations ==========

  async addToOrganization(card: ActionCard): Promise<void> {
    if (!this.selectedOrganization) {
      this.errorMessage = 'Please select an organization first.';
      return;
    }

    if (card.isConfigured) {
      this.errorMessage = 'This action is already configured for this organization.';
      return;
    }

    try {
      const md = new Metadata();
      const orgAction = await md.GetEntityObject<IzzyOrganizationActionEntity>('Organization Actions');

      orgAction.OrganizationID = this.selectedOrganization.ID;
      orgAction.ActionID = card.izzyAction.ActionID;
      orgAction.Status = 'Active';
      orgAction.Sequence = this.organizationActions.length;

      const result = await orgAction.Save();
      if (!result) {
        throw new Error('Failed to save organization action');
      }

      // Refresh data
      await IzzyMetadataEngine.Instance.Config(true);
      this.organizationActions = IzzyMetadataEngine.Instance.GetOrganizationActions(this.selectedOrganization.ID);
      this.buildActionCards();

      this.showSuccess(`Added "${card.izzyAction.Name}" to organization`);

      this.actionSelected.emit({
        izzyAction: card.izzyAction,
        action: 'add'
      });
    } catch (error) {
      console.error('Error adding action to organization:', error);
      this.errorMessage = 'Failed to add action. Please try again.';
    }
  }

  viewAction(card: ActionCard): void {
    this.actionSelected.emit({
      izzyAction: card.izzyAction,
      action: 'view'
    });
  }

  // ========== UI Helpers ==========

  getPlanBadgeClass(planLevel: number): string {
    if (planLevel === 0) return 'plan-free';
    if (planLevel <= 10) return 'plan-starter';
    if (planLevel <= 20) return 'plan-pro';
    return 'plan-enterprise';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Coming Soon': return 'status-coming-soon';
      case 'Deprecated': return 'status-deprecated';
      default: return '';
    }
  }

  getCategoryActionCount(node: CategoryNode): number {
    let count = node.actions.filter(a => a.izzyAction.Status === 'Active').length;
    for (const child of node.children) {
      count += this.getCategoryActionCount(child);
    }
    return count;
  }

  // ========== State Management ==========

  private emitStateChange(): void {
    this.stateChange.emit({
      selectedOrganizationId: this.selectedOrganization?.ID,
      selectedCategoryId: this.selectedCategory?.ID,
      searchTerm: this.searchTerm
    });
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadActionGalleryComponent(): void {
  // Intentionally empty
}
